import type { ErrorLike } from '@apollo/client';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import CategoryBreakdownRadarSection from '../../shared/CategoryBreakdownRadarSection';
import CognitiveFitness from '../../shared/CognitiveFitness';
import InsightSection from '../../shared/InsightSection';
import PerformanceInfo from '../../shared/PerformanceInfo';
import PhysiologicalFitnessSection from '../../shared/PhysiologicalFitnessSection';
import ProfileInformation from '../../shared/ProfileInformation';
import ReportHeader from '../../shared/ReportHeader';
import type {
    CognitiveFitness as CognitiveFitnessType,
    ReportHeaderData,
    Profile,
    PhysiologicalResult,
    PsychologicalResult,
    Performance,
} from '../../shared/model';
import Empty from '@/components/blocks/Empty';
import EmptyContainer from '@/components/common/EmptyContainer';
import Loader from '@/components/common/Loader';
import Section from '@/components/common/Section';

interface OverallTraineeReportContentProps {
    data: {
        loading: boolean;
        error: ErrorLike | undefined;
        hasAnyAssessmentItems: boolean;
        profile: Profile;
        reportHeaderData: ReportHeaderData;
        trainerNotes: string;
        physiologicalFitness: PhysiologicalResult[];
        psychologicalFitness: PsychologicalResult[];
        cognitiveFitness: CognitiveFitnessType[];
        performance: Performance[];
    };
    optimizeForPdf?: boolean;
    editMode?: boolean;
    isLite?: boolean;
    insightLoading?: boolean;
    insightError?: boolean;
    children?: React.ReactNode;
}

const OverallTraineeReportContent = memo(
    ({
        data,
        optimizeForPdf = false,
        isLite = false,
        insightLoading,
        insightError,
        children,
    }: OverallTraineeReportContentProps) => {
        const { t } = useTranslation(['overallTraineeReport']);
        const {
            loading,
            error,
            hasAnyAssessmentItems,
            profile,
            reportHeaderData,
            trainerNotes,
            physiologicalFitness,
            psychologicalFitness,
            cognitiveFitness,
            performance,
        } = data;

        const liteTitle = isLite ? t('liteReport.header.reportTitle', { name: profile.name }) : undefined;

        if (error) {
            return (
                <EmptyContainer>
                    <Empty title={t('empty.title')} />
                </EmptyContainer>
            );
        }

        if (loading && !hasAnyAssessmentItems) {
            return <Loader />;
        }

        const reportContent = (
            <div className="mt-10 w-full space-y-6 overflow-auto">
                <ReportHeader reportHeaderData={reportHeaderData} name={profile.name} title={liteTitle} />

                {!isLite && (
                    <>
                        {performance && performance.length > 0 ? (
                            <div className="grid gap-6 sm:grid-cols-12">
                                <ProfileInformation profile={profile} className="sm:col-span-4" />
                                <PerformanceInfo performance={performance} className="sm:col-span-8" />
                            </div>
                        ) : (
                            <ProfileInformation profile={profile} expanded />
                        )}
                    </>
                )}

                {/* Physiological Fitness */}
                {physiologicalFitness.length > 0 && (
                    <Section
                        title={t('sections.physiological.title')}
                        subtitle={t('sections.physiological.subtitle')}
                        accent="muted"
                    >
                        <PhysiologicalFitnessSection
                            physiologicalFitness={physiologicalFitness}
                            optimizeForPdf={optimizeForPdf}
                        />
                    </Section>
                )}

                {/* Psychological Strength */}
                {psychologicalFitness.length > 0 && (
                    <Section
                        title={t('sections.psychological.title')}
                        subtitle={t('sections.psychological.subtitle')}
                        accent="muted"
                    >
                        <div className="space-y-5">
                            {psychologicalFitness.map(m => (
                                <CategoryBreakdownRadarSection
                                    key={m.moduleDefinitionIdentifier}
                                    title={m.name}
                                    subtitle={m.description}
                                    iconKey={m.moduleDefinitionIdentifier}
                                    accent="blue"
                                    radarGroups={m.radarGroups ?? []}
                                />
                            ))}
                        </div>
                    </Section>
                )}

                {/* Cognitive Agility */}
                {cognitiveFitness.length > 0 && (
                    <Section
                        title={t('sections.cognitive.title')}
                        subtitle={t('sections.cognitive.subtitle')}
                        accent="muted"
                    >
                        <div className="space-y-5">
                            {cognitiveFitness.map(test => (
                                <CognitiveFitness
                                    key={test.moduleDefinitionIdentifier}
                                    name={test.name}
                                    description={test.description}
                                    moduleDefinitionIdentifier={test.moduleDefinitionIdentifier}
                                    result={test.result}
                                    note={test.note}
                                />
                            ))}
                        </div>
                    </Section>
                )}

                <InsightSection
                    isLite={isLite}
                    content={trainerNotes}
                    loading={insightLoading}
                    hasError={insightError}
                />
            </div>
        );

        return (
            <>
                {children}
                {reportContent}
            </>
        );
    }
);

OverallTraineeReportContent.displayName = 'OverallTraineeReportContent';

export default OverallTraineeReportContent;
